import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  ApiResponse,
  ExpenseClaimListItem,
  ExpenseClaimListResponse,
  ExpenseClaimVm,
  ExpenseClaimLineVm,
  ExpenseClaimLookupItem,
  ExpenseClaimSavePayload,
} from '@/api/types'

type ExpenseClaimLineFiles = Record<number, File | undefined>

function normalizeLookup(item: any): ExpenseClaimLookupItem {
  const percent =
    item.taxPercent ??
    item.TaxPercent ??
    item.taxRatePercent ??
    item.TaxRatePercent ??
    item.taxRate ??
    item.TaxRate

  const rate =
    item.taxRate ??
    item.TaxRate ??
    item.taxRatePercent ??
    item.TaxRatePercent ??
    item.taxPercent ??
    item.TaxPercent

  return {
    id: item.id ?? item.Id ?? item.taxRateId ?? item.TaxRateId ?? 0,
    name:
      item.name ??
      item.Name ??
      item.lookupNameEn ??
      item.LookupNameEn ??
      item.nameEn ??
      item.NameEn ??
      item.taxRateNameEn ??
      item.TaxRateNameEn,
    nameEn: item.nameEn ?? item.NameEn ?? item.lookupNameEn ?? item.LookupNameEn ?? item.taxRateNameEn ?? item.TaxRateNameEn,
    nameAr: item.nameAr ?? item.NameAr ?? item.lookupNameAr ?? item.LookupNameAr ?? item.taxRateNameAr ?? item.TaxRateNameAr,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
    taxPercent: percent,
    taxRate: rate,
  }
}

function normalizeListItem(row: any): ExpenseClaimListItem {
  return {
    id: row.id ?? row.Id ?? 0,
    claimDate: row.claimDate ?? row.ClaimDate ?? row.expenseDate ?? row.ExpenseDate ?? null,
    amount: row.amount ?? row.Amount ?? row.total ?? row.Total,
    detail: row.detail ?? row.Detail ?? row.details ?? row.Details,
    status: row.status ?? row.Status,
    statusName: row.statusName ?? row.StatusName,
    approved: row.approved ?? row.Approved ?? null,
    approvedDate: row.approvedDate ?? row.ApprovedDate ?? null,
    departmentId: row.departmentId ?? row.DepartmentId,
    departmentName: row.departmentName ?? row.DepartmentName,
    employeeId: row.employeeId ?? row.EmployeeId,
    employeeName: row.employeeName ?? row.EmployeeName,
    claimTypeId: row.claimTypeId ?? row.ClaimTypeId,
    claimTypeName: row.claimTypeName ?? row.ClaimTypeName,
  }
}

class ExpenseClaimService {
  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  private buildSaveFormData(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): FormData {
    const formData = new FormData()

    // Master fields
    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'EmpId', payload.empId)
    this.appendFormField(formData, 'DepartmentId', payload.departmentId)
    this.appendFormField(formData, 'ClaimType', payload.claimType)
    this.appendFormField(formData, 'Title', payload.title)
    this.appendFormField(formData, 'SpendDate', payload.spendDate)
    this.appendFormField(formData, 'ClaimDate', payload.claimDate)
    this.appendFormField(formData, 'Amount', payload.amount)
    this.appendFormField(formData, 'BranchId', payload.branchId)
    this.appendFormField(formData, 'Details', payload.details)

    // Detail lines
    payload.expenseClaimD.forEach((line, index) => {
      this.appendFormField(formData, `ExpenseClaimD[${index}].LineId`, line.lineId)
      this.appendFormField(formData, `ExpenseClaimD[${index}].InvoiceDate`, line.invoiceDate)
      this.appendFormField(formData, `ExpenseClaimD[${index}].Description`, line.description)
      this.appendFormField(formData, `ExpenseClaimD[${index}].Price`, line.price)
      this.appendFormField(formData, `ExpenseClaimD[${index}].VatPercent`, line.vatPercent)
      this.appendFormField(formData, `ExpenseClaimD[${index}].VatAmount`, line.vatAmount)
      this.appendFormField(formData, `ExpenseClaimD[${index}].LineAmount`, line.lineAmount)
      this.appendFormField(formData, `ExpenseClaimD[${index}].ClaimId`, line.claimId)
      this.appendFormField(formData, `ExpenseClaimD[${index}].CreatedDate`, line.createdDate)
      this.appendFormField(formData, `ExpenseClaimD[${index}].CreatedBy`, line.createdBy)
      this.appendFormField(formData, `ExpenseClaimD[${index}].UpdatedById`, line.updatedById)
      this.appendFormField(formData, `ExpenseClaimD[${index}].UpdatedDate`, line.updatedDate)
      const file = lineFiles[index]
      if (file) {
        // Some backends bind to Attachment, others to AttachmentFile; send both for compatibility.
        formData.append(`ExpenseClaimD[${index}].Attachment`, file, file.name)
        formData.append(`ExpenseClaimD[${index}].AttachmentFile`, file, file.name)
      } else {
        this.appendFormField(formData, `ExpenseClaimD[${index}].Attachment`, line.attachment)
        this.appendFormField(formData, `ExpenseClaimD[${index}].AttachmentFile`, line.attachmentFile ?? '')
      }
      this.appendFormField(formData, `ExpenseClaimD[${index}].IsEditable`, line.isEditable)
      this.appendFormField(formData, `ExpenseClaimD[${index}].Category`, line.category)
      this.appendFormField(formData, `ExpenseClaimD[${index}].IsDeleted`, line.isDeleted)
      this.appendFormField(formData, `ExpenseClaimD[${index}].BranchId`, line.branchId)
    })

    return formData
  }

  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<ExpenseClaimListResponse> {
    const url = endpoints.expenseClaim.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<ExpenseClaimListResponse> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    if (raw && typeof raw === 'object') {
      const arr = Array.isArray(raw.data) ? raw.data : Array.isArray(raw) ? raw : []
      return {
        totalRecords: raw.totalRecords ?? payload.totalRecords ?? arr.length ?? 0,
        pageNumber: raw.pageNumber ?? payload.pageNumber ?? pageNumber,
        pageSize: raw.pageSize ?? payload.pageSize ?? pageSize,
        data: (arr as any[]).map(normalizeListItem),
      }
    }
    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getById(id: number, branchId: number): Promise<ExpenseClaimVm | null> {
    const url = endpoints.expenseClaim.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = response.data
    const raw = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload
    const obj = Array.isArray(raw) ? raw[0] : raw
    if (!obj || typeof obj !== 'object') return null
    return this.normalizeVm(obj, branchId)
  }

  async getLinesById(id: number, branchId: number): Promise<ExpenseClaimLineVm[]> {
    const url = endpoints.expenseClaim.getLinesById(id, branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map((line: any) => ({
      id: line.id ?? line.Id,
      expenseClaimId: line.expenseClaimId ?? line.ExpenseClaimId,
      invoiceDate: line.invoiceDate ?? line.InvoiceDate ?? '',
      expenseTypeId: line.expenseTypeId ?? line.ExpenseTypeId ?? 0,
      description: line.description ?? line.Description ?? '',
      taxableAmount: line.taxableAmount ?? line.TaxableAmount ?? 0,
      taxRateId: line.taxRateId ?? line.TaxRateId ?? 0,
      taxPercent: line.taxPercent ?? line.TaxPercent ?? 0,
      taxAmount: line.taxAmount ?? line.TaxAmount ?? 0,
      amount: line.amount ?? line.Amount ?? 0,
      attachmentUrl: line.attachmentUrl ?? line.AttachmentUrl,
    }))
  }

  async getDepartments(_branchId: number): Promise<ExpenseClaimLookupItem[]> {
    // Use Employee/GetDepartment (no parameters) as in other modules
    const url = endpoints.employeeLookups.departments
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  async getEmployees(branchId: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getEmployees(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map((e: any) => {
      const fullName =
        e.fullName ??
        e.FullName ??
        e.employeeName ??
        e.EmployeeName ??
        [e.empFirstNameEn ?? e.EmpFirstNameEn, e.empFatherNameEn ?? e.EmpFatherNameEn, e.empSecondNameEn ?? e.EmpSecondNameEn, e.empLastNameEn ?? e.EmpLastNameEn]
          .filter(Boolean)
          .join(' ')
          .trim()
      return {
        id: e.id ?? e.Id ?? e.employeeId ?? e.EmployeeId ?? 0,
        name: fullName || (e.name ?? e.Name),
        nameEn: e.nameEn ?? e.NameEn,
        nameAr: e.nameAr ?? e.NameAr,
        lookupNameEn: e.lookupNameEn ?? e.LookupNameEn,
        lookupNameAr: e.lookupNameAr ?? e.LookupNameAr,
        departmentId: e.empDepartmetId ?? e.EmpDepartmetId ?? e.departmentId ?? e.DepartmentId,
      }
    })
  }

  async getTaxRates(branchId: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getTaxRates(branchId)
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list.map(normalizeLookup)
  }

  /**
   * GET /api/ExpenseClaimRequest/GetExpenseTypes — no branch segment (Swagger).
   * branchId kept for call-site compatibility; unused in URL.
   */
  async getExpenseTypes(_branchId?: number): Promise<ExpenseClaimLookupItem[]> {
    const url = endpoints.expenseClaim.getExpenseTypes
    const response = await apiClient.get<ApiResponse<any[]> | any>(url)
    const payload = response.data
    const list: any[] = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return list
      .filter((item: any) => {
        const active = item.isActive ?? item.IsActive
        if (active === undefined || active === null) return true
        return active === 1 || active === true
      })
      .map((item: any) => {
        const en = item.lookupNameEn ?? item.LookupNameEn ?? item.name ?? item.Name ?? ''
        const ar = item.lookupNameAr ?? item.LookupNameAr ?? ''
        const id = item.id ?? item.Id ?? 0
        return {
          id,
          name: en || ar || `Type ${id}`,
          nameEn: en,
          nameAr: ar,
          lookupNameEn: item.lookupNameEn ?? item.LookupNameEn,
          lookupNameAr: item.lookupNameAr ?? item.LookupNameAr,
        }
      })
  }

  async save(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): Promise<any> {
    const formData = this.buildSaveFormData(payload, lineFiles)
    const response = await apiClient.post(endpoints.expenseClaim.save, formData)
    return response.data
  }

  async update(payload: ExpenseClaimSavePayload, lineFiles: ExpenseClaimLineFiles = {}): Promise<any> {
    const formData = this.buildSaveFormData(payload, lineFiles)
    const response = await apiClient.put(endpoints.expenseClaim.update, formData)
    return response.data
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.expenseClaim.delete(id)
    const response = await apiClient.delete(url)
    return response.data
  }

  private normalizeVm(raw: any, branchId: number): ExpenseClaimVm {
    return {
      id: raw.id ?? raw.Id ?? 0,
      branchId: raw.branchId ?? raw.BranchId ?? branchId,
      departmentId: raw.departmentId ?? raw.DepartmentId ?? 0,
      employeeId: raw.employeeId ?? raw.EmployeeId ?? raw.empId ?? raw.EmpId ?? 0,
      amount: raw.amount ?? raw.Amount ?? 0,
      details: raw.details ?? raw.Details ?? raw.detail ?? raw.Detail ?? '',
      claimDate: raw.claimDate ?? raw.ClaimDate ?? '',
      claimTypeId: raw.claimTypeId ?? raw.ClaimTypeId,
      claimType: raw.claimType ?? raw.ClaimType,
      expenseDate: raw.expenseDate ?? raw.ExpenseDate ?? raw.claimDate ?? raw.ClaimDate ?? '',
      expenseClaimLines: Array.isArray(raw.expenseClaimLines ?? raw.ExpenseClaimLines)
        ? (raw.expenseClaimLines ?? raw.ExpenseClaimLines).map((l: any) => ({
            id: l.id ?? l.Id,
            expenseClaimId: l.expenseClaimId ?? l.ExpenseClaimId,
            invoiceDate: l.invoiceDate ?? l.InvoiceDate ?? '',
            expenseTypeId: l.expenseTypeId ?? l.ExpenseTypeId ?? 0,
            description: l.description ?? l.Description ?? '',
            taxableAmount: l.taxableAmount ?? l.TaxableAmount ?? 0,
            taxRateId: l.taxRateId ?? l.TaxRateId ?? 0,
            taxPercent: l.taxPercent ?? l.TaxPercent ?? 0,
            taxAmount: l.taxAmount ?? l.TaxAmount ?? 0,
            amount: l.amount ?? l.Amount ?? 0,
            attachmentUrl: l.attachmentUrl ?? l.AttachmentUrl,
          }))
        : [],
      subTotal: raw.subTotal ?? raw.SubTotal,
      totalVat: raw.totalVat ?? raw.TotalVat ?? raw.totalTax ?? raw.TotalTax,
      total: raw.total ?? raw.Total,
    }
  }
}

export const expenseClaimService = new ExpenseClaimService()
